import React, { useEffect, useRef, useState } from "react";
import { ListCommanWay } from "API/commonApi";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import Select from "react-select";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import {
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { Button } from "@heroui/react";
import Spinner from "utils/Spinner";
import { GenericColumnData } from "utils/TableColumnData";

// Compact select styles (ToPayPickup / CreditReport style)
const pickupSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 2px",
    minHeight: "32px",
    fontSize: "12px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: "32px" }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "12px",
    padding: "4px 10px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
  singleValue: (base: any) => ({ ...base, color: "#1e293b", fontSize: "12px" }),
};

const PAYMENT_TYPE_OPTIONS = [
  { value: "EXPENSE", label: "EXPENSE" },
  { value: "COMPANY_EXPENSE", label: "COMPANY_EXPENSE" },
  { value: "SALARY", label: "SALARY" },
  { value: "VENDOR", label: "VENDOR" },
  { value: "KM_VOUCHER", label: "KM_VOUCHER" },
];

const STATUS_OPTIONS = [
  { value: "PAID", label: "PAID" },
  { value: "NOT_PAID", label: "Not PAID" },
];

const FALLBACK_DEBIT_ACCOUNTS: Record<string, string> = {
  "1": "Ahmedabad Branch",
  "2": "Delhi Branch",
  "3": "Mumbai Branch",
  "4": "Gondal Branch",
};

const getDebitAccountName = (key: any) => {
  if (!key) return "-";
  const strKey = String(key);
  const debitAccOptions =
    GenericColumnData?.user?.tableColumnData?.find(
      (d: any) => d.key === "personalData.debitAcc"
    )?.options || [];

  const found = debitAccOptions.find((opt: any) => String(opt.value) === strKey);
  if (found?.label) return found.label;

  return FALLBACK_DEBIT_ACCOUNTS[strKey] || `Branch #${strKey}`;
};

const InitiatedPaymentsList: React.FC<any> = () => {
  const tableRef = useRef<any>(null);

  const [tableData, setTableData] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<any>(moment().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").format("YYYY-MM-DD"));

  const [selectedPaymentType, setSelectedPaymentType] = useState<any>();
  const [selectedStatus, setSelectedStatus] = useState<any>();
  const [uniqueRequestId, setUniqueRequestId] = useState<string>("");

  const [sortStat, setSortStat] = useState<any>({});

  const [query, setQuery] = useState<any>({
    createdAt: {
      $gte: moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      $lte: moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    },
  });

  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_payment_transaction_list"],
    () =>
      ListCommanWay("/admin/payment-transaction/list", query, {
        paginate: 1000,
        order: [['createdAt', 'DESC']]
      }),
    {
      keepPreviousData: true,
      onSuccess(data: any) {
        if (data?.status === "SUCCESS") {
          const respData = data?.data;
          const docs = respData?.docs || respData?.data || (Array.isArray(respData) ? respData : []);
          setTableData(docs);
        } else {
          setTableData([]);
        }
      },
      onError() {
        setTableData([]);
      },
    }
  );

  const __handleSubmit = () => {
    let q: any = {};
    if (startDate && endDate) {
      q.createdAt = {
        $gte: moment(startDate).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
        $lte: moment(endDate).endOf("day").format("YYYY-MM-DD HH:mm:ss"),
      };
    }
    if (selectedPaymentType?.value) {
      q.paymentType = selectedPaymentType.value;
    }
    if (selectedStatus?.value) {
      if (selectedStatus.value === "PAID") {
        q.status = "PAID";
      } else if (selectedStatus.value === "NOT_PAID") {
        q.status = { $ne: "PAID" };
      }
    }
    if (uniqueRequestId.trim()) {
      q.uniqueRequestId = { $regex: uniqueRequestId.trim(), $options: "i" };
    }
    setQuery(q);
  };

  useEffect(() => {
    refetch();
  }, [query]);

  const handleSort = (key: any, info: any) => {
    let data = [...tableData];
    let order = sortStat[key] === "asc" ? "desc" : "asc";

    let sortedData = data.sort((a: any, b: any) => {
      let valueA = key.split(".").reduce((obj: any, k: string) => (obj ? obj[k] : undefined), a);
      let valueB = key.split(".").reduce((obj: any, k: string) => (obj ? obj[k] : undefined), b);

      if (valueA === undefined || valueA === null) valueA = "";
      if (valueB === undefined || valueB === null) valueB = "";

      const numA = Number(valueA);
      const numB = Number(valueB);
      
      if (!isNaN(numA) && !isNaN(numB) && valueA !== "" && valueB !== "") {
        return order === "asc" ? numA - numB : numB - numA;
      }

      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });

    let _sortStat = { ...sortStat };
    _sortStat[key] = order;
    setSortStat(_sortStat);
    setTableData(sortedData);
  };

  const GET_SORT_ICON = (key: any, info: any) => {
    switch (sortStat[key]) {
      case "asc":
        return <ArrowDownIcon className="h-4 inline-block" onClick={() => handleSort(key, info)} />;
      case "desc":
        return <ArrowUpIcon className="h-4 inline-block" onClick={() => handleSort(key, info)} />;
      default:
        return <ArrowsUpDownIcon className="h-4 inline-block" onClick={() => handleSort(key, info)} />;
    }
  };

  const exportToExcel = () => {
    const excelData = tableData.map((r, index) => ({
      "Sr No": index + 1,
      "Created At": r.createdAt ? moment(r.createdAt).format("DD-MM-YYYY HH:mm:ss") : "-",
      "User": r.beneficiary_name || r.beneName || "-",
      "Debit Account": getDebitAccountName(r.debitAccountKey),
      "Amount": Number(r.amount || 0),
      "Payment Method": r.paymentMethod || "-",
      "Payment Type": r.paymentType || "-",
      "Status": r.status || "-",
      "Batch No": r.batchNo || "-",
      "Unique Request ID": r.uniqueRequestId || "-",
      "UTR No": r.utrNo || "-",
      "Error Message": r.errorMessage || "-",
      "Paid Date": r.paidDate ? moment(r.paidDate).format("DD-MM-YYYY HH:mm:ss") : "-",
    }));

    const ws = utils.json_to_sheet(excelData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Initiated Payments");
    const wbout = write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "Initiated_Payments.xlsx");
  };

  const totalAmountSum = tableData.reduce((acc, row) => acc + (Number(row.amount) || 0), 0);

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter min-h-screen">
      {(isFetching || isLoading) && <Spinner />}

      {/* Title & Total Header Card */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between">
              <div></div>

            {/* Centered Gradient Title */}
            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              INITIATED PAYMENTS
            </h1>

            <div />
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl shadow-slate-200/50">
          <div className="flex flex-wrap items-end gap-3">
            {/* Payment Type Filter */}
            <div className="min-w-[160px]">
              <label className="block mb-1 text-[11px] font-medium text-slate-600">Payment Type</label>
              <Select
                styles={pickupSelectStyles}
                options={PAYMENT_TYPE_OPTIONS}
                onChange={(e) => setSelectedPaymentType(e)}
                value={selectedPaymentType}
                isClearable={true}
                placeholder="All Types"
                menuPortalTarget={document.body}
              />
            </div>

            {/* Status Filter */}
            <div className="min-w-[140px]">
              <label className="block mb-1 text-[11px] font-medium text-slate-600">Status</label>
              <Select
                styles={pickupSelectStyles}
                options={STATUS_OPTIONS}
                onChange={(e) => setSelectedStatus(e)}
                value={selectedStatus}
                isClearable={true}
                placeholder="All Statuses"
                menuPortalTarget={document.body}
              />
            </div>

            {/* Unique Request ID Filter */}
            <div className="min-w-[150px]">
              <label className="block mb-1 text-[11px] font-medium text-slate-600">Unique Request ID</label>
              <input
                type="text"
                className="bg-white/90 border border-slate-200 text-slate-900 text-xs rounded-[10px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block w-full p-2 h-8"
                placeholder="Request ID"
                value={uniqueRequestId}
                onChange={(e) => setUniqueRequestId(e.target.value)}
              />
            </div>

            {/* Date Range */}
            <AfDateRangePicker setStartDate={setStartDate} setEndDate={setEndDate} label="Start - End Date" />

            {/* Action Buttons */}
            <Button
              size="sm"
              color="success"
              className="h-8 rounded-lg font-semibold text-white"
              type="button"
              onPress={__handleSubmit}
              startContent={<MagnifyingGlassIcon className="w-4 h-4" />}
            >
              Search
            </Button>

            <Button
              size="sm"
              variant="flat"
              color="primary"
              className="h-8 rounded-lg font-semibold"
              startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
              onPress={exportToExcel}
            >
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="mx-auto">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden">
          {/* Table Content */}
          <div className="overflow-x-auto">
            {!isLoading && !isFetching && (!tableData || !Array.isArray(tableData) || tableData.length === 0) && (
              <div className="flex flex-col items-center py-12">
                <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <p className="text-slate-500 font-medium">No data found</p>
                <p className="text-slate-400 text-sm">Try adjusting your filters</p>
              </div>
            )}

            {tableData && tableData.length > 0 && (
              <table className="w-full border-collapse" ref={tableRef} id="myTable" style={{ fontSize: "85%" }}>
                <thead className="sticky top-0 z-10 bg-slate-100">
                  <tr className="ignore-excel">
                    <th className="border border-slate-200 px-2 bg-slate-100 text-left text-xs font-bold text-slate-700" colSpan={5}>
                      Results &nbsp;|&nbsp; <span className="text-primary font-semibold">{tableData.length} entries</span>
                    </th>
                    <th className="border border-slate-200 px-1 bg-slate-100 text-right text-xs font-bold text-slate-700">
                      {Number(totalAmountSum || 0).toFixed(2)}
                    </th>
                    <th className="border border-slate-200 px-1 bg-slate-100" colSpan={7}></th>
                  </tr>
                  <tr>
                    <th className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase bg-slate-200">Sr.</th>
                    <th
                      className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-300 bg-slate-200 whitespace-nowrap"
                      onClick={() => handleSort("createdAt", "asc")}
                    >
                      Created At {GET_SORT_ICON("createdAt", "asc")}
                    </th>
                    <th
                      className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-300 bg-slate-200 whitespace-nowrap"
                      onClick={() => handleSort("beneficiary_name", "asc")}
                    >
                      User {GET_SORT_ICON("beneficiary_name", "asc")}
                    </th>
                    <th className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase bg-slate-200 whitespace-nowrap">
                      Debit Account
                    </th>
                    <th
                      className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-300 bg-slate-200 whitespace-nowrap"
                      onClick={() => handleSort("paymentType", "asc")}
                    >
                      Payment Type {GET_SORT_ICON("paymentType", "asc")}
                    </th>
                    <th
                      className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-300 bg-slate-200 whitespace-nowrap"
                      onClick={() => handleSort("amount", "asc")}
                    >
                      Amount {GET_SORT_ICON("amount", "asc")}
                    </th>
                    <th className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase bg-slate-200 whitespace-nowrap">
                      Method
                    </th>
                    <th
                      className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-300 bg-slate-200 whitespace-nowrap"
                      onClick={() => handleSort("status", "asc")}
                    >
                      Status {GET_SORT_ICON("status", "asc")}
                    </th>
                    <th className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase bg-slate-200 whitespace-nowrap">
                      Batch No
                    </th>
                    <th className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase bg-slate-200 whitespace-nowrap">
                      Unique Request ID
                    </th>
                    <th className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase bg-slate-200 whitespace-nowrap">
                      UTR No
                    </th>
                    <th className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase bg-slate-200 whitespace-nowrap">
                      Error Message
                    </th>
                    <th
                      className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-300 bg-slate-200 whitespace-nowrap"
                      onClick={() => handleSort("paidDate", "asc")}
                    >
                      Paid Date {GET_SORT_ICON("paidDate", "asc")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((r: any, i: number) => {
                    return (
                      <tr key={"r_" + (r.id || i)} className="hover:bg-slate-50 transition-colors">
                        <td className="border border-slate-200 px-2">{i + 1}</td>
                        <td className="border border-slate-200 px-1">
                          {r.createdAt ? moment(r.createdAt).format("DD-MM-YYYY HH:mm:ss") : "-"}
                        </td>
                        <td className="border border-slate-200 px-1">
                          {r.beneficiary_name || r.beneName || "-"}
                        </td>
                        <td className="border border-slate-200 px-1">
                          {getDebitAccountName(r.debitAccountKey)}
                        </td>
                        <td className="border border-slate-200 px-1 font-semibold text-slate-700">
                          {r.paymentType || "-"}
                        </td>
                        <td className="border border-slate-200 px-1 text-right">
                          ₹{Number(r.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-slate-200 px-1">
                          {r.paymentMethod || "-"}
                        </td>
                        <td className="border border-slate-200 px-1">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${
                              r.status === "PAID"
                                ? "bg-green-100 text-green-800"
                                : r.status === "FAILED" || r.status === "REJECTED"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-amber-100 text-amber-800"

                            }`}
                          >
                            {r.status || "-"}
                          </span>
                        </td>
                        <td className="border border-slate-200 px-1 font-mono">
                          {r.batchNo || "-"}
                        </td>
                        <td className="border border-slate-200 px-1 font-mono">
                          {r.uniqueRequestId || "-"}
                        </td>
                        <td className="border border-slate-200 px-1 font-mono">
                          {r.utrNo || "-"}
                        </td>
                        <td className="border border-slate-200 px-1 text-red-600 max-w-xs truncate" title={r.errorMessage || ""}>
                          {r.errorMessage || "-"}
                        </td>
                        <td className="border border-slate-200 px-1">
                          {r.paidDate ? moment(r.paidDate).format("DD-MM-YYYY HH:mm:ss") : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InitiatedPaymentsList;
